import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import type { SceneCopy, ProductLabel } from '../content';
import { Scrubber } from '../engine/scrubber';
import { SequenceScrubber } from '../engine/sequence';
import { ChapterFallback } from '../engine/fallbacks';
import { MEDIA_VERSION, TIER_SPECS } from '../engine/tiers';
import { recordChapterLoad } from '../rum';
import type { SceneContext, SceneHandle } from './types';

gsap.registerPlugin(ScrollTrigger);

/**
 * Generic pinned chapter, playback mode by tier:
 *  - desktop: canvas image-sequence scrub (Apple-style; no <video> seek
 *    pipeline). Falls back to the mobile-tier video scrub if the sequence
 *    fails. After the pin completes the scene HOLDS the final (opened) frame.
 *  - mobile ("cinematic"): the dive autoplays once on arrival and
 *    holds its last frame while labels read.
 */
export interface ChapterOptions {
  /** Desktop pin travel (default '+=160%'). */
  pinEnd?: string;
  /** Cinematic (mobile) pin travel (default '+=100%'). */
  cinematicPinEnd?: string;
  /**
   * Remap scroll progress before frame lookup (default identity). E.g. an
   * ease-in makes a shot spend most of its scroll on the early frames and
   * accelerate through the end (outro warp-out).
   */
  progressEase?: (p: number) => number;
}

export function mountChapter(
  copy: SceneCopy & { products: ProductLabel[] },
  ctx: SceneContext,
  opts: ChapterOptions = {},
): SceneHandle {
  const section = ctx.root.querySelector<HTMLElement>(`#scene-${copy.id}`);
  if (!section) throw new Error(`#scene-${copy.id} missing`);

  const media = section.querySelector<HTMLElement>('.scene-media');
  const poster = section.querySelector<HTMLImageElement>('.scene-poster');
  const video = section.querySelector<HTMLVideoElement>('.scene-video');
  const retryBtn = section.querySelector<HTMLButtonElement>('.scene-retry');
  const errorEl = section.querySelector<HTMLElement>('.scene-error');
  const labels = section.querySelectorAll<HTMLElement>('.product-label');

  if (!media || !poster || !video) {
    throw new Error(`${copy.title} media nodes missing`);
  }

  const desktop = ctx.tier === 'desktop';
  // Poster/fallback-video tier: desktop's video tier is retired (the sequence
  // replaces it), so anything video on desktop uses the mobile encodes.
  const fileTier = desktop ? 'mobile' : ctx.tier;
  // Portrait phones get the 9:16 reframed encodes (scripts/encode-portrait.sh)
  // instead of a center crop of the 16:9 shot. Chosen at mount; a mid-visit
  // rotate keeps the current framing until reload.
  const portrait =
    !desktop && window.matchMedia('(orientation: portrait)').matches;
  const fileBase = portrait ? 'mobile-portrait' : fileTier;
  const posterUrl = `assets/${copy.id}/${fileBase}.webp?v=${MEDIA_VERSION}`;
  const endPosterUrl = `assets/${copy.id}/${fileBase}-end.webp?v=${MEDIA_VERSION}`;
  const videoUrl = `assets/${copy.id}/${fileBase}.mp4?v=${MEDIA_VERSION}`;
  const loadId = copy.id;
  const cinematic = !desktop;

  poster.src = posterUrl;
  poster.alt = `${copy.title} poster`;

  const ease = opts.progressEase ?? ((p: number): number => p);
  let scrubber: Scrubber | null = null;
  let sequence: SequenceScrubber | null = null;
  let seqReady = false;
  let seqFailed = false;
  let seqPromise: Promise<void> | null = null;
  /** Prefetch progress fan-out — listeners may attach after prefetch starts. */
  const progressListeners: Array<(ratio: number) => void> = [];
  let lastRatio = 0;
  let pinSt: ScrollTrigger | null = null;
  let mediaReady = false;
  /** True once the user has scrolled past this scene's pin (holds end frame). */
  let passed = false;
  const tweens: gsap.core.Tween[] = [];
  const cleanups: Array<() => void> = [];

  if (ctx.reducedMotion || ctx.staticMode) {
    video.setAttribute('aria-hidden', 'true');
    video.style.visibility = 'hidden';
    // Static mode shows the END frame — every ship open, page fully readable.
    if (ctx.staticMode) poster.src = endPosterUrl;
    poster.classList.add('rm-fade-in');
    labels.forEach((el) => el.classList.add('rm-fade-in', 'is-visible'));
    return {
      section,
      video,
      scrubber: null,
      dispose: () => cleanups.forEach((fn) => fn()),
    };
  }

  if (cinematic) {
    video.loop = false;
  }
  video.setAttribute('aria-hidden', 'true');

  /** Desktop primary path: prefetch + decode the image sequence. */
  const ensureSequence = (
    onProgress?: (ratio: number) => void,
  ): Promise<void> => {
    if (!desktop || seqFailed) return Promise.resolve();
    if (onProgress) {
      // Late attach is normal: the pin trigger usually starts the prefetch
      // before the loading gate registers its listener.
      progressListeners.push(onProgress);
      onProgress(lastRatio);
    }
    if (!seqPromise) {
      sequence = new SequenceScrubber(
        `assets/${copy.id}/seq`,
        media,
        `?v=${MEDIA_VERSION}`,
      );
      const t0 = performance.now();
      seqPromise = sequence
        .prefetch((ratio) => {
          lastRatio = ratio;
          progressListeners.forEach((fn) => fn(ratio));
        })
        .then(() => {
          seqReady = true;
          poster.classList.add('is-scrubbing');
          recordChapterLoad(copy.id, performance.now() - t0, true);
        })
        .catch(() => {
          // Sequence unavailable — retire it and scrub the fallback video.
          seqFailed = true;
          sequence?.dispose();
          sequence = null;
          recordChapterLoad(copy.id, performance.now() - t0, false);
          void ensureVideo().catch(() => undefined);
        });
    }
    return seqPromise;
  };

  /** Video path: cinematic tiers always; desktop only as sequence fallback. */
  const ensureVideo = async (): Promise<void> => {
    if (desktop && !seqFailed) return;
    if (mediaReady && ctx.loader.getManaged(loadId)?.state === 'ready') return;
    const t0 = performance.now();
    try {
      if (desktop && !scrubber) {
        scrubber = new Scrubber(video, {
          poster: posterUrl,
          durationHint: TIER_SPECS[fileTier].durationSec,
        });
      }
      await ctx.loader.load(videoUrl, video, { id: loadId });
      recordChapterLoad(copy.id, performance.now() - t0, true);
      poster.classList.add('is-scrubbing');
      mediaReady = true;
      fallback.recover();
      if (cinematic) {
        // Arriving fresh: play the dive once. Returning from below: jump to
        // the held end frame instead of replaying the reveal.
        if (passed) {
          video.currentTime = video.duration || 0;
        } else {
          video.currentTime = 0;
          void video.play().catch(() => undefined);
        }
      }
    } catch (err) {
      mediaReady = false;
      recordChapterLoad(copy.id, performance.now() - t0, false);
      // Internal cancellations (load superseded or evicted by the loader) are
      // not user-facing failures — visibility re-requests the media. Only
      // real load errors get the error card.
      const msg = err instanceof Error ? err.message : '';
      if (/^(Load (disposed|cancelled)|Loader disposed)/.test(msg)) return;
      fallback.enterError(msg || `${copy.title} media failed`);
      throw err;
    }
  };

  const ensureMedia = (
    onProgress?: (ratio: number) => void,
  ): Promise<void> => {
    return desktop && !seqFailed
      ? ensureSequence(onProgress)
      : ensureVideo().catch(() => undefined);
  };

  if (!cinematic) {
    scrubber = null; // sequence is the primary; Scrubber built only on fallback
  } else {
    scrubber = new Scrubber(video, {
      poster: posterUrl,
      durationHint: TIER_SPECS[fileTier].durationSec,
    });
  }

  const fallback = new ChapterFallback(
    { posterEl: poster, retryBtn, videoEl: video, errorEl },
    { onRetry: () => ensureVideo() },
  );

  labels.forEach((el, i) => {
    gsap.set(el, { autoAlpha: 0, y: 24 });
    const tw = gsap.to(el, {
      autoAlpha: 1,
      y: 0,
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: `top+=${18 + i * 18}% top`,
        end: `top+=${36 + i * 18}% top`,
        scrub: 0.5,
        onEnter: () => el.classList.add('is-visible'),
      },
    });
    tweens.push(tw);
  });

  pinSt = ScrollTrigger.create({
    trigger: section,
    // Fire ~2.5 viewports early: media downloads while the PREVIOUS scene is
    // still pinned, so the first pass plays/scrubs ready frames.
    start: 'top 250%',
    end: 'bottom top',
    onEnter: () => {
      void ensureMedia();
    },
    onEnterBack: () => {
      void ensureMedia();
    },
  });

  const videoEvicted = (): boolean => {
    if (ctx.loader.getManaged(loadId)?.state !== 'ready') {
      mediaReady = false;
      poster.src = passed ? endPosterUrl : posterUrl;
      poster.classList.remove('is-scrubbing');
      void ensureVideo().catch(() => undefined);
      return true;
    }
    return false;
  };

  const pinScrub = ScrollTrigger.create({
    trigger: section,
    start: 'top top',
    end: cinematic
      ? (opts.cinematicPinEnd ?? '+=100%')
      : (opts.pinEnd ?? '+=160%'),
    pin: true,
    scrub: 0.5,
    onToggle: (self) => {
      // Shield the active scene's fallback video from prefetch eviction.
      if (self.isActive) {
        ctx.loader.protect(loadId);
      } else if (ctx.loader.getProtected() === loadId) {
        ctx.loader.protect(null);
      }
    },
    onLeave: () => {
      // Scrolled past: hold the final (opened) frame from here on.
      passed = true;
      poster.src = endPosterUrl;
      if (desktop && seqReady) sequence?.setProgress(ease(1));
      if (desktop && seqFailed && mediaReady) scrubber?.setProgress(1);
    },
    onLeaveBack: () => {
      // Scrolled back above the scene: next entry replays the reveal.
      passed = false;
      poster.src = posterUrl;
      if (desktop && seqReady) sequence?.setProgress(0);
      if (desktop && seqFailed && mediaReady) scrubber?.setProgress(0);
      if (cinematic && mediaReady) {
        video.pause();
        video.currentTime = 0;
      }
    },
    onEnter: () => {
      if (cinematic && mediaReady && !videoEvicted() && !passed) {
        video.currentTime = 0;
        void video.play().catch(() => undefined);
      }
    },
    onEnterBack: () => {
      if (cinematic && mediaReady && !videoEvicted()) {
        video.currentTime = video.duration || 0;
      }
    },
    onUpdate: (self) => {
      if (cinematic) return;
      if (seqReady) {
        sequence?.setProgress(ease(self.progress));
        return;
      }
      // Fallback video scrub (sequence failed or still loading).
      if (!seqFailed || !mediaReady) return;
      if (videoEvicted()) return;
      scrubber?.setProgress(self.progress);
      try {
        fallback.markGoodFrame(video.currentTime);
      } catch {
        fallback.holdLastFrame();
      }
    },
  });

  cleanups.push(() => {
    pinSt?.kill();
    pinScrub.kill();
    tweens.forEach((t) => t.kill());
    scrubber?.dispose();
    sequence?.dispose();
    ctx.loader.disposeById(loadId);
  });

  return {
    section,
    video,
    scrubber,
    warmup: (onProgress) => ensureMedia(onProgress),
    dispose: () => cleanups.forEach((fn) => fn()),
  };
}
